// Tickets equivalent of calendar/functions/saveEventToSupabase.js. Uses the
// generic upsertRow / softDeleteRow helpers from shared-gov so we don't
// reinvent the optimistic-id + error-handling logic per app.
//
// All three are bound as element methods (registered via functions/index.js)
// so callers do `el.call('saveTicketToSupabase', ticket)` and the active
// fetch context is reused.

import { GITHUB_SYNC_URL, GITHUB_SYNC_HEADERS } from '../../config.js'
import { upsertRow, softDeleteRow, isOptimisticId } from '../supabaseRow.js'

const TABLE = 'tickets'
const _logErr = (scope, err) => console.error(`[${scope}]`, err?.message || err)

const _toRow = (t, currentUserEmail) => {
  // Carry forward existing metadata so we don't clobber other fields (e.g.
  // readyForTest, agent_test set by the QA toggle on the create form).
  const prevMeta = t.metadata && typeof t.metadata === 'object' ? t.metadata : {}
  const metadata = { ...prevMeta }
  if (t.agentTest !== undefined) metadata.agent_test = !!t.agentTest
  return {
    ...(typeof t.id === 'number' && !isOptimisticId(t.id) ? { id: t.id } : {}),
    title: t.title || '(no title)',
    body: t.body || null,
    column_key: t.columnKey || 'backlog',
    position: t.position || 0,
    assignee_email: t.assigneeEmail || null,
    reporter_email: t.reporterEmail || currentUserEmail,
    labels: t.labels || [],
    priority: t.priority || null,
    due_date: t.dueDate || null,
    github_issue_url: t.githubUrl || null,
    github_node_id: t.githubNodeId || null,
    metadata,
    updated_at: new Date().toISOString(),
    deleted: false,
    dirty: true
  }
}

// Fire-and-forget — UI shouldn't block on the GitHub round-trip. Wrapped
// in try/catch so any synchronous throw (missing fetch, invalid URL) is
// swallowed too. Async failures are swallowed via the .catch.
export const triggerGithubSync = () => {
  try {
    fetch(GITHUB_SYNC_URL, { method: 'POST', headers: GITHUB_SYNC_HEADERS }).catch(() => {})
  } catch { /* ignore */ }
}

export const saveTicketToSupabase = async function (ticket) {
  const row = _toRow(ticket, this.state?.root?.currentUserEmail)
  const id = await upsertRow.call(this, { from: TABLE, row })
  if (id != null) triggerGithubSync()
  return id
}

export const deleteTicketFromSupabase = async function (ticketId) {
  const ok = await softDeleteRow.call(this, { from: TABLE, id: ticketId })
  if (ok) triggerGithubSync()
  return ok
}

// Move ticket between columns / reorder. Touches only the columns + position
// fields so the trigger flips `dirty` for the github-sync push.
export const moveTicket = async function ({ ticketId, columnKey, position }) {
  const fetch = this.context?.fetch
  if (!fetch?.update) { _logErr('moveTicket', 'context.fetch.update missing'); return false }
  if (isOptimisticId(ticketId)) return true
  try {
    const result = await fetch.update({
      from: TABLE,
      params: { id: ticketId },
      data: { column_key: columnKey, position, updated_at: new Date().toISOString() }
    })
    if (result?.error) { _logErr('moveTicket', result.error); return false }
    triggerGithubSync()
    return true
  } catch (e) { _logErr('moveTicket', e); return false }
}
